import * as React from 'react';
import { FormField, InputAdornment, InputSlider } from '@cian/ui-kit';
import { MortgageCalculator as MortgageCalculatorComponent } from '../../components/MortgageCalculator';
import { Labels, Summary } from '../../components/MortgageCalculator/parts';
import { BanksSlider } from '../BanksSlider';
import { EMortgageCalculatorType, initialState, reducer } from './reducer';
import { getLoanPayment } from './helpers';
import { creditTermOptions, downPaymentOptions, mortgageConfig, rateOptions } from './config';

type THandlerType = keyof typeof mortgageConfig | 'downPaymentLabels';

export function MortgageCalculator() {
  const { propertyCost, downPayment, creditTerm, rate } = mortgageConfig;
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const loanPayment = React.useMemo(
    () => getLoanPayment({ term: state.creditTerm, rate: state.rate, loan: state.propertyCost - state.downPayment }),
    [state],
  );
  const requiredIncome = React.useMemo(() => Math.ceil(loanPayment / 0.6).toLocaleString('ru'), [loanPayment]);

  const handleSliderChange = React.useCallback(
    (type: THandlerType) => (value: number) => {
      const handler: Record<THandlerType, () => void> = {
        propertyCost: () => dispatch({ type: EMortgageCalculatorType.PropertyCostChange, payload: value }),
        downPayment: () =>
          dispatch({
            type: EMortgageCalculatorType.DownPaymentChange,
            payload: value > state.propertyCost * 0.9 ? state.downPayment : value,
          }),
        downPaymentLabels: () =>
          dispatch({ type: EMortgageCalculatorType.DownPaymentChange, payload: state.propertyCost * (value / 100) }),
        creditTerm: () => dispatch({ type: EMortgageCalculatorType.CreditTermChange, payload: value }),
        rate: () => dispatch({ type: EMortgageCalculatorType.RateChange, payload: value }),
      };

      handler[type]();
    },
    [state.downPayment, state.propertyCost],
  );

  return (
    <MortgageCalculatorComponent
      propertyCost={
        <FormField label="Стоимость недвижимости">
          <InputSlider
            min={propertyCost.min}
            max={propertyCost.max}
            step={propertyCost.step}
            value={state.propertyCost}
            onChange={handleSliderChange('propertyCost')}
            rightAdornment={<InputAdornment>{propertyCost.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      downPayment={
        <FormField label="Первоначальный взнос" withoutMargin>
          <InputSlider
            min={downPayment.min}
            max={downPayment.max}
            step={downPayment.step}
            value={state.downPayment}
            onChange={handleSliderChange('downPayment')}
            rightAdornment={<InputAdornment>{downPayment.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      downPaymentLabels={<Labels options={downPaymentOptions} onClick={handleSliderChange('downPaymentLabels')} />}
      creditTerm={
        <FormField label="Срок кредита" withoutMargin>
          <InputSlider
            min={creditTerm.min}
            max={creditTerm.max}
            step={creditTerm.step}
            value={state.creditTerm}
            onChange={handleSliderChange('creditTerm')}
            rightAdornment={<InputAdornment>{creditTerm.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      creditTermLabels={<Labels options={creditTermOptions} onClick={handleSliderChange('creditTerm')} />}
      rate={
        <FormField label="Процентная ставка" withoutMargin>
          <InputSlider
            min={rate.min}
            max={rate.max}
            step={rate.step}
            value={state.rate}
            onChange={handleSliderChange('rate')}
            rightAdornment={<InputAdornment>{rate.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      rateLabels={<Labels options={rateOptions} onClick={handleSliderChange('rate')} />}
      summary={
        <Summary
          loanPayment={`${loanPayment.toLocaleString('ru')} ₽`}
          requiredIncome={`${requiredIncome} ₽`}
          banks={<BanksSlider />}
        />
      }
    />
  );
}
